import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { fmtMoney, fmtDate } from "../lib/format";
import { Coins, TrendingUp, Wallet, MapPin, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

function currentMonthKey() {
  return new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().slice(0, 7);
}

const EMPTY_RULE = { city: "", mode: "per_document", rate: 0.6, threshold: 500 };

export default function Commissions() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState(currentMonthKey);
  const [cityReport, setCityReport] = useState(null);
  const [documentCities, setDocumentCities] = useState([]);
  const [ruleForm, setRuleForm] = useState([]);
  const [savingRules, setSavingRules] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [delivererNames, setDelivererNames] = useState({});
  const isAdmin = user?.role === "admin";
  const canPay = user?.role === "admin" || user?.role === "operator";

  async function loadCommissions(nextPeriod = period) {
    const { data: city } = await api.get(`/commissions/by-city?period_key=${nextPeriod}`);
    setCityReport(city);
    const [{ data: i }, { data: s }] = await Promise.all([
      api.get("/commissions"),
      api.get("/commissions/summary"),
    ]);
    setItems(i || []);
    setSummary(s);
    if (user?.role !== "deliverer") {
      try {
        const { data: users } = await api.get("/users?role=deliverer");
        const names = {};
        for (const u of users || []) names[u.id] = u.name || u.email;
        setDelivererNames(names);
      } catch { /* ignore */ }
    }
  }

  useEffect(() => {
    (async () => {
      try {
        await loadCommissions(period);
        const { data: cfg } = await api.get("/commissions/city-rules");
        setDocumentCities(cfg?.cities || []);
        setRuleForm((cfg?.rules || []).map((r) => ({
          city: r.city,
          mode: r.mode,
          rate: r.rate,
          threshold: r.threshold,
        })));
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar comissões");
      }
    })();
  }, [period]);

  const cityRows = (cityReport?.deliverers || []).flatMap((d) =>
    (d.cities || [])
      .filter((city) => city.documents > 0)
      .map((city) => ({ ...city, deliverer_id: d.deliverer_id, name: d.name }))
  );
  const showDeliverer = user?.role !== "deliverer";
  const unusedCities = documentCities.filter((city) => !ruleForm.some((r) => r.city === city));
  const pendingItems = items.filter((c) => c.status === "pending" && Number(c.amount) > 0);

  async function payCommission(id) {
    setPayingId(id);
    try {
      await api.patch(`/commissions/${id}/pay`);
      toast.success(t("commissions_paid_ok"));
      await loadCommissions(period);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("commissions_paid_failed"));
    } finally {
      setPayingId(null);
    }
  }

  async function payPending() {
    if (!pendingItems.length) return;
    setPayingId("bulk");
    try {
      const { data } = await api.post("/commissions/pay", { ids: pendingItems.map((c) => c.id) });
      toast.success(t("commissions_paid_bulk").replace("{count}", String(data?.paid || 0)));
      await loadCommissions(period);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("commissions_paid_failed"));
    } finally {
      setPayingId(null);
    }
  }

  function updateRule(index, patch) {
    setRuleForm((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  async function saveRules() {
    setSavingRules(true);
    try {
      const { data } = await api.put("/commissions/city-rules", {
        rules: ruleForm.map((r) => ({
          city: r.city,
          mode: r.mode,
          rate: Number(r.rate),
          threshold: Number(r.threshold),
        })),
      });
      setRuleForm((data?.rules || []).map((r) => ({
        city: r.city,
        mode: r.mode,
        rate: r.rate,
        threshold: r.threshold,
      })));
      await loadCommissions(period);
      toast.success(t("commissions_city_saved"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("commissions_city_save_failed"));
    } finally {
      setSavingRules(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4">
        <div className="text-xs text-[var(--text-2)] font-medium">Remuneração</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">{t("nav_commissions")}</h1>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { icon: Coins, label: t("commissions_pending"), value: fmtMoney(summary.pending), sub: `${summary.pending_count} ${t("commissions_entries")}`, accent: "text-[var(--warning-text)]", iconBg: "bg-[var(--warning-soft)] text-[var(--warning-text)]" },
            { icon: Wallet, label: t("commissions_paid"), value: fmtMoney(summary.paid), sub: `${summary.paid_count} ${t("commissions_entries")}`, accent: "text-[var(--success-text)]", iconBg: "bg-[var(--success-soft)] text-[var(--success-text)]" },
            { icon: TrendingUp, label: t("accumulated"), value: fmtMoney(summary.pending + summary.paid), sub: "Total no período", accent: "text-[var(--text-1)]", iconBg: "bg-[var(--brand-soft)] text-[var(--brand)]" },
          ].map((k, i) => (
            <div key={i} className="card-soft p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-2xl ${k.iconBg} flex items-center justify-center`}>
                  <k.icon size={18} />
                </div>
              </div>
              <div className="text-xs text-[var(--text-2)] font-medium">{k.label}</div>
              <div className={`font-display text-3xl font-bold mt-1 ${k.accent}`}>{k.value}</div>
              <div className="text-xs text-[var(--text-2)] mt-1">{k.sub}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card-soft p-5 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-[var(--brand)]" />
              <h2 className="font-display text-lg font-bold m-0">{t("commissions_city_title")}</h2>
            </div>
            <p className="text-xs text-[var(--text-2)] mt-1 max-w-3xl">{t("commissions_city_hint")}</p>
          </div>
          <label className="text-xs text-[var(--text-2)] font-medium">
            {t("commissions_period")}
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="mt-1 block h-9 px-3 rounded-xl border border-[var(--border-c)] bg-[var(--surface)] text-sm text-[var(--text-1)]"
              data-testid="commission-city-period"
            />
          </label>
        </div>

        {isAdmin && (
          <div className="rounded-xl border border-[var(--border-c)] p-4 space-y-3">
            <div className="text-sm font-semibold">{t("commissions_city_rules")}</div>
            <p className="text-xs text-[var(--text-2)]">{t("commissions_city_source")}</p>
            {ruleForm.map((row, index) => (
              <div key={`${row.city}-${index}`} className="grid gap-2 md:grid-cols-[1.4fr_1.2fr_0.8fr_0.8fr_auto] items-end">
                <label className="text-[11px] text-[var(--text-2)] font-medium">
                  {t("city")}
                  <select
                    value={row.city}
                    onChange={(e) => updateRule(index, { city: e.target.value })}
                    className="mt-1 block w-full h-9 px-3 rounded-xl border border-[var(--border-c)] bg-[var(--surface)] text-sm"
                    data-testid={`commission-city-select-${index}`}
                  >
                    {!documentCities.includes(row.city) && row.city && (
                      <option value={row.city}>{row.city}</option>
                    )}
                    {documentCities.map((city) => (
                      <option key={city} value={city} disabled={ruleForm.some((r, i) => i !== index && r.city === city)}>
                        {city}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-[11px] text-[var(--text-2)] font-medium">
                  {t("commissions_city_basis")}
                  <select
                    value={row.mode}
                    onChange={(e) => updateRule(index, { mode: e.target.value })}
                    className="mt-1 block w-full h-9 px-3 rounded-xl border border-[var(--border-c)] bg-[var(--surface)] text-sm"
                  >
                    <option value="per_address">{t("commissions_city_mode_address")}</option>
                    <option value="per_document">{t("commissions_city_mode_document")}</option>
                  </select>
                </label>
                <label className="text-[11px] text-[var(--text-2)] font-medium">
                  {t("amount")}
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={row.rate}
                    onChange={(e) => updateRule(index, { rate: e.target.value })}
                    className="mt-1 block w-full h-9 px-3 rounded-xl border border-[var(--border-c)] bg-[var(--surface)] text-sm"
                  />
                </label>
                <label className="text-[11px] text-[var(--text-2)] font-medium">
                  {t("commissions_city_fixed")}
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={row.threshold}
                    onChange={(e) => updateRule(index, { threshold: e.target.value })}
                    className="mt-1 block w-full h-9 px-3 rounded-xl border border-[var(--border-c)] bg-[var(--surface)] text-sm"
                    data-testid={`commission-city-threshold-${index}`}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setRuleForm((current) => current.filter((_, i) => i !== index))}
                  className="h-9 w-9 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--danger-soft)] hover:text-[var(--danger)] flex items-center justify-center"
                  aria-label={t("commissions_city_remove")}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!unusedCities.length}
                onClick={() => setRuleForm((current) => [...current, { ...EMPTY_RULE, city: unusedCities[0] }])}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-[var(--surface-2)] text-xs font-semibold disabled:opacity-40"
              >
                <Plus size={14} /> {t("commissions_city_add")}
              </button>
              <button
                type="button"
                disabled={savingRules || !ruleForm.length}
                onClick={saveRules}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-[var(--brand)] text-white text-xs font-semibold disabled:opacity-40"
                data-testid="commission-city-save"
              >
                {savingRules ? t("saving") : t("save")}
              </button>
            </div>
            {!documentCities.length && (
              <p className="text-xs text-[var(--warning-text)]">{t("commissions_city_no_documents")}</p>
            )}
          </div>
        )}

        {!isAdmin && (
          <div className="grid gap-2 md:grid-cols-2">
            {(cityReport?.rules || []).map((rule) => (
              <div key={rule.city_key} className="rounded-xl bg-[var(--surface-2)] px-4 py-3 text-sm">
                <div className="font-semibold">
                  {rule.city} — {rule.mode === "per_address" ? t("commissions_city_mode_address") : t("commissions_city_mode_document")}
                </div>
                <div className="text-xs text-[var(--text-2)] mt-1">
                  {fmtMoney(rule.rate)} / {rule.unit_label} · {t("commissions_city_fixed")} {rule.threshold}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)] border-b border-[var(--border-c)]">
              <tr className="text-left text-xs text-[var(--text-2)] font-semibold">
                {showDeliverer && <th className="px-4 py-3">{t("deliverer")}</th>}
                <th className="px-4 py-3">{t("city")}</th>
                <th className="px-4 py-3">{t("commissions_city_basis")}</th>
                <th className="px-4 py-3 text-right">{t("commissions_city_documents")}</th>
                <th className="px-4 py-3 text-right">{t("commissions_city_units")}</th>
                <th className="px-4 py-3 text-right">{t("commissions_city_fixed")}</th>
                <th className="px-4 py-3 text-right">{t("commissions_city_excess")}</th>
                <th className="px-4 py-3 text-right">{t("amount")}</th>
              </tr>
            </thead>
            <tbody>
              {cityRows.length === 0 && (
                <tr>
                  <td colSpan={showDeliverer ? 8 : 7} className="text-center py-8 text-[var(--text-2)]">
                    {t("commissions_city_empty")}
                  </td>
                </tr>
              )}
              {cityRows.map((row) => (
                <tr key={`${row.deliverer_id}-${row.city_key}`} className="border-b border-[var(--border-c)]">
                  {showDeliverer && <td className="px-4 py-3">{row.name}</td>}
                  <td className="px-4 py-3 font-medium">{row.city}</td>
                  <td className="px-4 py-3 text-xs text-[var(--text-2)]">
                    {row.mode === "per_address" ? t("commissions_city_mode_address") : t("commissions_city_mode_document")}
                    {" · "}{fmtMoney(row.rate)}/{row.unit_label}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{row.documents}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.units} {row.unit_label}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.threshold}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.excess}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{fmtMoney(row.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-soft overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--border-c)] flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold">{t("commissions_list")}</div>
          {canPay && pendingItems.length > 0 && (
            <button
              type="button"
              disabled={!!payingId}
              onClick={payPending}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-[var(--brand)] text-white text-xs font-semibold disabled:opacity-40"
              data-testid="commission-pay-pending"
            >
              <Check size={14} /> {t("commissions_pay_pending")}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)] border-b border-[var(--border-c)]">
              <tr className="text-left text-xs text-[var(--text-2)] font-semibold">
                <th className="px-4 py-3">Referência</th>
                <th className="px-4 py-3">Entregador</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3">Período</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">{t("created")}</th>
                {canPay && <th className="px-4 py-3 text-right">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={canPay ? 7 : 6} className="text-center py-10 text-[var(--text-2)]">{t("no_data")}</td></tr>}
              {items.map((c) => (
                <tr key={c.id} className="border-b border-[var(--border-c)] hover:bg-[var(--surface-2)]" data-testid={`commission-${c.id}`}>
                  <td className="px-4 py-3 text-xs">
                    {c.notes || (c.document_id ? c.document_id.slice(0, 8) : "Bônus")}
                  </td>
                  <td className="px-4 py-3 text-sm">{delivererNames[c.deliverer_id] || c.deliverer_id?.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{fmtMoney(c.amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-2)]">{c.period_key || "—"}</td>
                  <td className="px-4 py-3"><span className={`pill ${c.status === "paid" ? "pill-success" : "pill-warning"}`}>{c.status === "paid" ? "Pago" : "Pendente"}</span></td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-2)]">{fmtDate(c.created_at)}</td>
                  {canPay && (
                    <td className="px-4 py-3 text-right">
                      {c.status === "pending" && Number(c.amount) > 0 && (
                        <button
                          type="button"
                          disabled={!!payingId}
                          onClick={() => payCommission(c.id)}
                          className="inline-flex items-center gap-1 px-2.5 h-8 rounded-xl bg-[var(--surface-2)] text-xs font-semibold hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] disabled:opacity-40"
                          data-testid={`commission-pay-${c.id}`}
                        >
                          <Check size={12} /> {t("mark_paid")}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
